import { Backdrop, makeStyles, Modal } from '@material-ui/core'
import { Notification, NotificationGroup } from '@progress/kendo-react-notification'
import { useEffect, useState } from 'react'
import styles from './NotificationManager.module.scss'
import {
    BUTTON_TYPES,
    //FastQuoteButton
} from '../Inputs/Inputs'
import { ButtonInfo, CustomButtonDialog, Dialog } from '../GenericModals/GenericModals'
import { Observer } from '../_base/Observer'

const useStyles = makeStyles((theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 0,
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
    },
}))

type ConfirmAlertInfo = {
    type: 'confirm'
    message: string | JSX.Element
    onConfirm(): void
    onCancel(): void
    title: string
}
type ButtonModalInfo = {
    type: 'buttons'
    message: string | JSX.Element
    buttons: ButtonInfo[]
    title: string
}
type ErrorAlertInfo = {
    type: 'error'
    message?: string | JSX.Element
    title: string
}
interface AlertInfoBase {
    logLevel?: 'debug' | 'production'
}
type AlertInfo = AlertInfoBase &
    (
        | {
              type: 'success' | 'warning' | 'info' | 'none'
              message: string | JSX.Element
          }
        | ConfirmAlertInfo
        | ErrorAlertInfo
        | ButtonModalInfo
    )
const alertObserver = new Observer<AlertInfo>()
export const fqAlert = (info: AlertInfo) => alertObserver.fire(info)
export const fqConfirm = async ({
    title,
    message,
}: {
    title: string
    message: string | JSX.Element
}): Promise<boolean> =>
    await new Promise((resolve, reject) =>
        fqAlert({
            type: 'confirm',
            title: title,
            message: message,
            onCancel: () => resolve(false),
            onConfirm: () => resolve(true),
        })
    )
export const fqConfirmAllowNo = async ({
    title,
    message,
    showCancel,
}: {
    title: string
    message: string
    showCancel?: boolean
}): Promise<boolean | null> =>
    await new Promise((resolve, reject) =>
        fqAlert({
            type: 'buttons',
            title: title,
            message: message,
            buttons: [
                { onClick: () => resolve(true), type: BUTTON_TYPES.PRIMARY, text: 'Yes' },
                { onClick: () => resolve(false), type: BUTTON_TYPES.SECONDARY, text: 'No' },
                ...(showCancel !== false
                    ? [
                          {
                              onClick: () => resolve(null),
                              type: BUTTON_TYPES.SECONDARY,
                              text: 'Cancel',
                          },
                      ]
                    : []),
            ],
        })
    )
interface ModalProps {
    close(): void
    messageInfo: AlertInfo
}
interface ConfirmModalProps extends ModalProps {
    messageInfo: ConfirmAlertInfo
}

const ConfirmModal = (props: ConfirmModalProps) => {
    const { close, messageInfo } = props
    const { onConfirm, onCancel, title, message } = messageInfo
    return (
        <Dialog
            onCancel={() => {
                onCancel()
                close()
            }}
            onConfirm={() => {
                onConfirm()
                close()
            }}
            title={title}
            style={{ maxWidth: 600, minWidth: 300 }}
        >
            {typeof message === 'string' ? (
                <ul style={{ whiteSpace: 'pre-wrap', fontWeight: 300, paddingInlineStart: 20 }}>
                    {(message || '')
                        .split('\n')
                        .map((row, i) => row && <li key={`messageLI${i}`}>{row}</li>)}
                </ul>
            ) : (
                <div style={{ paddingInlineStart: 20 }}>message</div>
            )}
        </Dialog>
    )
}
interface ButtonModalProps extends ModalProps {
    messageInfo: ButtonModalInfo
}

const ButtonModal = (props: ButtonModalProps) => {
    const { close, messageInfo } = props
    const { title, message, buttons } = messageInfo
    return (
        <CustomButtonDialog
            onClose={() => {
                close()
            }}
            buttons={buttons.map((row) => ({
                ...row,
                onClick: () => {
                    row.onClick()
                    close()
                },
            }))}
            title={title}
            style={{ maxWidth: 600, minWidth: 300 }}
        >
            {typeof message === 'string' ? (
                <ul style={{ whiteSpace: 'pre-wrap', fontWeight: 300, paddingInlineStart: 20 }}>
                    {(message || '')
                        .split('\n')
                        .map((row, i) => row && <li key={`messageLI${i}`}>{row}</li>)}
                </ul>
            ) : (
                <div style={{ paddingInlineStart: 20 }}>{message}</div>
            )}
        </CustomButtonDialog>
    )
}
interface ErrorModalProps extends ModalProps {
    messageInfo: ErrorAlertInfo
}
const ErrorMessageModal = (props: ErrorModalProps) => {
    const { messageInfo, close } = props
    const classes = useStyles()

    return (
        <Modal
            className={`${classes.modal} ${styles.maxZIndex}`}
            onClose={() => {
                close()
            }}
            open={true}
            BackdropComponent={Backdrop}
        >
            <div className={styles.modal} data-testid={'recipientModal'}>
                <div className={styles.modalHeader}>
                    <div
                        className={styles.modalTitle}
                        style={{ fontSize: 18, color: '#EA0000', lineHeight: '24px' }}
                    >
                        <div className={'a-i-danger'} />
                        {'Error'}
                    </div>
                    <div onClick={close} className={'a-i-close'} />
                </div>
                <div className={styles.modalBody}>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{messageInfo && messageInfo.title}</div>
                    {typeof messageInfo?.message === 'string' ? (
                        <ul
                            style={{
                                whiteSpace: 'pre-wrap',
                                fontWeight: 300,
                                paddingInlineStart: 20,
                            }}
                        >
                            {(messageInfo?.message || '')
                                .split('\n')
                                .map((row, i) => row && <li key={`messageLI${i}`}>{row}</li>)}
                        </ul>
                    ) : (
                        <div
                            style={{
                                paddingInlineStart: 20,
                            }}
                        >
                            message
                        </div>
                    )}

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: 25,
                        }}
                    >
                        <div
                            className={'a-cancel-button'}
                            style={{
                                width: 94,
                            }}
                            onClick={close}
                        >
                            Close
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
const logLevel = 'production'
export const NotificationManager = () => {
    const [messageInfo, setMessageInfo] = useState<AlertInfo>()
    const [modalOpen, setModalOpen] = useState(false)
    const handleAlert = (info: AlertInfo) => {
        let alertLogLevel = info.logLevel || 'production'
        if (alertLogLevel !== logLevel) {
            return
        }
        setMessageInfo(info)
        if (['confirm', 'error', 'buttons'].includes(info.type)) {
            setModalOpen(true)
        } else {
            setTimeout(() => setMessageInfo(undefined), 3000)
        }
    }
    useEffect(() => {
        alertObserver.subscribe(handleAlert)
        return () => alertObserver.unsubscribe(handleAlert)
    }, [])
    // const classes = useStyles()

    const close = () => setModalOpen(false)
    return (
        <>
            {/* <Modal
                open={modalOpen}
                className={`${classes.modal} ${styles.maxZIndex}`}
                onClose={() => {
                    close()
                }}
                BackdropComponent={Backdrop}
            > */}
            {modalOpen && (
                <>
                    {messageInfo && messageInfo.type === 'error' && (
                        <ErrorMessageModal close={close} messageInfo={messageInfo} />
                    )}
                    {messageInfo && messageInfo.type === 'confirm' && (
                        <ConfirmModal close={close} messageInfo={messageInfo} />
                    )}
                    {messageInfo && messageInfo.type === 'buttons' && (
                        <ButtonModal close={close} messageInfo={messageInfo} />
                    )}
                </>
            )}
            {/* </Modal> */}
            <NotificationGroup
                style={{
                    top: 0,
                    width: '100%',
                    margin: '0px auto',
                    alignItems: 'center',
                    // want it over modals
                    zIndex: 9999999999,
                }}
            >
                {messageInfo &&
                    messageInfo.type !== 'error' &&
                    messageInfo.type !== 'confirm' &&
                    messageInfo.type !== 'buttons' && (
                        <Notification
                            type={{ style: messageInfo.type, icon: false }}
                            style={{ overflow: 'visible' }}
                        >
                            <span>{messageInfo.message}</span>
                        </Notification>
                    )}
            </NotificationGroup>
        </>
    )
}
